import { Check, Plus } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Spinner from '@/components/shared/Spinner';
import type { Task } from '@/components/ui/columns-tasks';
import type { OutlookCalendar } from '@/hooks/useOutlookCalendars';
import { cn } from '@/lib/utils';

interface OutlookSyncCellProps {
  task: Task;
  availableCalendars: OutlookCalendar[];
  onSyncToCalendar: (taskGuid: string, calendarId: string) => Promise<boolean>;
  onUnsyncFromCalendar?: (taskGuid: string, calendarId: string, myCalendarId?: string) => Promise<boolean>;
  onMenuClose?: () => void | Promise<unknown>;
  disabled?: boolean;
}

type SyncedCalendar = NonNullable<Task['synced_calendars']>[number];

function calendarForSync(sync: SyncedCalendar, calendars: OutlookCalendar[]) {
  const byId = calendars.find((calendar) => calendar.id === sync.calendar_id);
  if (byId) return byId;
  if (!sync.owner_address) return undefined;
  const owner = sync.owner_address.toLowerCase();
  return calendars.find((calendar) => calendar.owner?.address?.toLowerCase() === owner);
}

function OutlookMark({ synced }: { synced: boolean }) {
  return (
    <span className="relative inline-flex h-5 w-5 items-center justify-center">
      <img
        src="/microsoft-outlook.svg"
        alt=""
        className={synced ? 'h-4 w-4 object-contain' : 'h-4 w-4 object-contain opacity-50 grayscale'}
      />
      <span
        className={
          synced
            ? 'absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full border border-white bg-blue-600 text-white'
            : 'absolute -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full border border-white bg-stone-400 text-white'
        }
      >
        {synced ? (
          <Check className="h-1.5 w-1.5" strokeWidth={3} />
        ) : (
          <Plus className="h-1.5 w-1.5" strokeWidth={3} />
        )}
      </span>
    </span>
  );
}

function calendarRows(synced: SyncedCalendar[], availableCalendars: OutlookCalendar[]) {
  const editable = availableCalendars.filter((calendar) => calendar.canEdit);
  const claimed = new Set<string>();
  const rows: Array<{
    key: string;
    name: string;
    calendarId: string;
    synced: boolean;
    sync?: SyncedCalendar;
  }> = [];

  for (const calendar of editable) {
    const sync = synced.find((entry) => {
      const match = calendarForSync(entry, availableCalendars);
      return match?.id === calendar.id || entry.calendar_id === calendar.id;
    });
    if (sync) claimed.add(sync.calendar_id);
    rows.push({
      key: calendar.id,
      name: calendar.name,
      calendarId: calendar.id,
      synced: Boolean(sync),
      sync,
    });
  }

  for (const sync of synced) {
    if (claimed.has(sync.calendar_id)) continue;
    const calendar = calendarForSync(sync, availableCalendars);
    rows.push({
      key: sync.calendar_id,
      name: calendar?.name ?? 'Outlook calendar',
      calendarId: calendar?.id ?? sync.calendar_id,
      synced: true,
      sync,
    });
  }

  return rows;
}

export function OutlookSyncCell({
  task,
  availableCalendars,
  onSyncToCalendar,
  onUnsyncFromCalendar,
  onMenuClose,
  disabled = false,
}: OutlookSyncCellProps) {
  const [open, setOpen] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(() => new Set());
  const [localSynced, setLocalSynced] = useState<SyncedCalendar[] | null>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inflightRef = useRef(0);
  const openRef = useRef(false);
  const synced = localSynced ?? task.synced_calendars ?? [];
  const rows = calendarRows(synced, availableCalendars);
  const isSynced = synced.length > 0;

  openRef.current = open;

  const refreshThenClear = () => {
    void Promise.resolve(onMenuClose?.()).finally(() => {
      setLocalSynced(null);
    });
  };

  const closeAndRefresh = () => {
    openRef.current = false;
    setOpen(false);
    if (inflightRef.current === 0) refreshThenClear();
  };

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const update = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const width = 256;
      const estimatedHeight = Math.min(288, 44 + rows.length * 36);
      const left = Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8);
      const openDown = rect.bottom + 4 + estimatedHeight <= window.innerHeight - 8;
      const top = openDown
        ? rect.bottom + 4
        : Math.max(8, rect.top - estimatedHeight - 4);
      setCoords({ top, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, rows.length]);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      const swallowClick = (clickEvent: MouseEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        document.removeEventListener('click', swallowClick, true);
      };
      document.addEventListener('click', swallowClick, true);
      closeAndRefresh();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeAndRefresh();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (task.is_deleted || rows.length === 0) {
    return <span className="text-xs text-gray-300">—</span>;
  }

  const markBusy = (id: string, busy: boolean) => {
    setBusyIds((current) => {
      const next = new Set(current);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const runChange = async (
    id: string,
    work: () => Promise<boolean>,
    apply: (current: SyncedCalendar[]) => SyncedCalendar[],
  ) => {
    inflightRef.current += 1;
    markBusy(id, true);
    try {
      const ok = await work();
      if (ok) {
        setLocalSynced((current) => apply(current ?? task.synced_calendars ?? []));
      }
    } finally {
      markBusy(id, false);
      inflightRef.current = Math.max(0, inflightRef.current - 1);
      if (!openRef.current && inflightRef.current === 0) refreshThenClear();
    }
  };

  const handleAdd = (calendarId: string) => {
    if (disabled || busyIds.has(calendarId)) return;
    void runChange(
      calendarId,
      () => onSyncToCalendar(task.id, calendarId),
      (current) =>
        current.some((entry) => entry.calendar_id === calendarId)
          ? current
          : [...current, { calendar_id: calendarId, provider: 'outlook', synced_at: new Date().toISOString() }],
    );
  };

  const handleRemove = (sync: SyncedCalendar) => {
    if (disabled || !onUnsyncFromCalendar || busyIds.has(sync.calendar_id)) return;
    const match = calendarForSync(sync, availableCalendars);
    const calendarId = sync.original_calendar_id || sync.calendar_id;
    const myCalendarId = sync.original_calendar_id
      ? sync.calendar_id
      : match && match.id !== calendarId
        ? match.id
        : undefined;
    void runChange(
      sync.calendar_id,
      () => onUnsyncFromCalendar(task.id, calendarId, myCalendarId),
      (current) => current.filter((entry) => entry.calendar_id !== sync.calendar_id),
    );
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          if (open) closeAndRefresh();
          else setOpen(true);
        }}
        className="inline-flex rounded-md p-0.5 transition-colors hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={isSynced ? 'Manage Outlook calendars' : 'Sync deadline to Outlook'}
      >
        <OutlookMark synced={isSynced} />
      </button>
      {open
        ? createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label="Outlook calendars"
              className="fixed z-[100000] w-64 rounded-md border border-stone-200 bg-white p-1 text-stone-950 shadow-md"
              style={{ top: coords.top, left: coords.left }}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">Outlook calendars</div>
              {rows.map((row) => {
                const busy = busyIds.has(row.calendarId) || (row.sync ? busyIds.has(row.sync.calendar_id) : false);
                const action = row.synced ? 'Remove' : 'Add';
                return (
                  <button
                    key={row.key}
                    type="button"
                    disabled={disabled || busy || (row.synced && !onUnsyncFromCalendar)}
                    aria-label={`${action} ${row.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (row.synced && row.sync) handleRemove(row.sync);
                      else handleAdd(row.calendarId);
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-stone-100 disabled:opacity-50',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{row.name}</span>
                    <span className="inline-flex h-4 min-w-12 shrink-0 items-center justify-end text-xs text-stone-400">
                      {busy ? <Spinner size="xs" /> : action}
                    </span>
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
